import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Award, Upload, Download, Trash2, RefreshCw, Plus, AlertTriangle, CheckCircle2,
  Clock, X, Calendar, Mail, FileText, Edit3
} from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  CERTIFICATES — legal / compliance docs with expiry tracking.
//  Admin uploads + gets email + StaffTask reminders 30/15/7/1 days
//  before expiry (cron runs daily 08:00 IST).
// ═══════════════════════════════════════════════════════════════════

const CATEGORIES = [
  { key: 'license',      label: 'License',       color: '#2563eb', icon: '📋' },
  { key: 'registration', label: 'Registration',  color: '#7c3aed', icon: '🆔' },
  { key: 'noc',          label: 'NOC',            color: '#16a34a', icon: '✅' },
  { key: 'insurance',    label: 'Insurance',      color: '#db2777', icon: '🛡️' },
  { key: 'mou',          label: 'MOU',            color: '#ea580c', icon: '🤝' },
  { key: 'other',        label: 'Other',          color: '#6b7280', icon: '📄' },
];

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const expiryBadge = (cert) => {
  if (cert.neverExpires) return { label: 'No Expiry', bg: '#e0e7ff', fg: '#3730a3' };
  if (!cert.expiryDate)  return { label: 'Unknown',   bg: '#f3f4f6', fg: '#4b5563' };
  const d = cert.daysUntilExpiry ?? Math.ceil((new Date(cert.expiryDate) - Date.now()) / 86400000);
  if (d < 0)    return { label: `Expired ${Math.abs(d)}d ago`, bg: '#fee2e2', fg: '#991b1b' };
  if (d <= 7)   return { label: `${d}d left — URGENT`,         bg: '#fee2e2', fg: '#991b1b' };
  if (d <= 30)  return { label: `${d}d left`,                   bg: '#fef3c7', fg: '#92400e' };
  if (d <= 90)  return { label: `${d}d left`,                   bg: '#dbeafe', fg: '#1e40af' };
  return        { label: `${d}d left`,                           bg: '#d1fae5', fg: '#065f46' };
};

const CertificatesPage = ({ API_BASE, currentUser }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [renewing, setRenewing] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [running, setRunning] = useState(false);

  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);
  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/certificates`);
      setList(res.data || []);
    } catch (e) { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return list;
    if (filter === 'expiring') {
      return list.filter(c => {
        if (c.neverExpires || !c.expiryDate) return false;
        const d = Math.ceil((new Date(c.expiryDate) - Date.now()) / 86400000);
        return d <= 30;
      });
    }
    return list.filter(c => c.category === filter);
  }, [list, filter]);

  const expiringCount = useMemo(() => {
    return list.filter(c => {
      if (c.neverExpires || !c.expiryDate) return false;
      const d = Math.ceil((new Date(c.expiryDate) - Date.now()) / 86400000);
      return d <= 30;
    }).length;
  }, [list]);

  const handleDelete = async (cert) => {
    if (!window.confirm(`"${cert.name}" archive karein?`)) return;
    try {
      await axios.delete(`${API_BASE}/certificates/${cert._id}`);
      showMsg('success', 'Archived');
      load();
    } catch (e) { showMsg('error', 'Delete failed'); }
  };

  const handleRunCheck = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API_BASE}/certificates/check-expiry`);
      showMsg('success', `Checked ${res.data.checked} certs · ${res.data.notifications} notifications sent`);
      load();
    } catch (e) { showMsg('error', 'Check failed'); }
    finally { setRunning(false); }
  };

  const downloadFile = (cert) => {
    window.open(`${API_BASE}/certificates/${cert._id}/file`, '_blank');
  };

  return (
    <div>
      {/* ─── HEADER ─── */}
      <div style={{ ...cardStyle, marginBottom: 16, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}>
              <Award size={22} style={{ verticalAlign: -4, marginRight: 6 }} />
              Certificates & Licenses
            </h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              {list.length} total · <strong>{expiringCount}</strong> expiring in 30 days · auto-reminders via email + staff task
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isAdmin && (
              <>
                <button onClick={handleRunCheck} disabled={running} style={headBtn}>
                  <RefreshCw size={14} style={{ marginRight: 4, ...(running ? { animation: 'spin 1s linear infinite' } : {}) }} />
                  {running ? 'Checking…' : 'Run Expiry Check'}
                </button>
                <button onClick={() => { setEditing(null); setShowForm(true); }} style={headBtn}>
                  <Plus size={14} style={{ marginRight: 4 }} /> Add Certificate
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {msg.text && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      {/* ─── FILTER PILLS ─── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        <button onClick={() => setFilter('all')} style={pill(filter === 'all')}>
          All <span style={pillCount}>{list.length}</span>
        </button>
        <button onClick={() => setFilter('expiring')} style={pill(filter === 'expiring', '#dc2626')}>
          <AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: -2 }} />
          Expiring Soon <span style={pillCount}>{expiringCount}</span>
        </button>
        {CATEGORIES.map(c => {
          const count = list.filter(x => x.category === c.key).length;
          return (
            <button key={c.key} onClick={() => setFilter(c.key)} style={pill(filter === c.key, c.color)}>
              <span style={{ marginRight: 4 }}>{c.icon}</span>{c.label} <span style={pillCount}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* ─── LIST ─── */}
      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60, color: theme.textMuted }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>
          <Award size={44} color={theme.textSubtle} style={{ marginBottom: 10, opacity: 0.5 }} />
          <p style={{ color: theme.textMuted, fontWeight: 600 }}>Koi certificate nahi mila</p>
          <p style={{ fontSize: 13, color: theme.textSubtle }}>Upar se "Add Certificate" click karke upload karein</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {filtered.map(cert => {
            const badge = expiryBadge(cert);
            const cat = CATEGORIES.find(c => c.key === cert.category) || CATEGORIES[5];
            return (
              <div key={cert._id} style={{ ...cardStyle, padding: 14, borderLeft: `4px solid ${cat.color}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: cat.color, background: cat.color + '18', padding: '2px 7px', borderRadius: 4 }}>
                        {cat.icon} {cat.label}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, background: badge.bg, color: badge.fg, padding: '2px 7px', borderRadius: 4 }}>
                        {badge.label}
                      </span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14.5, color: theme.navy, lineHeight: 1.3 }}>{cert.name}</div>
                    {cert.certNumber && <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 2 }}>#{cert.certNumber}</div>}
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 3, fontSize: 12, color: theme.textMuted, marginBottom: 10 }}>
                  {cert.issuer && <div><strong style={{ color: theme.navy }}>Issuer:</strong> {cert.issuer}</div>}
                  {cert.issuedDate && <div><Calendar size={11} style={{ verticalAlign: -1, marginRight: 3 }} />Issued: {fmt(cert.issuedDate)}</div>}
                  {cert.expiryDate && <div><Clock size={11} style={{ verticalAlign: -1, marginRight: 3 }} />Expires: {fmt(cert.expiryDate)}</div>}
                  {cert.neverExpires && <div><CheckCircle2 size={11} style={{ verticalAlign: -1, marginRight: 3 }} />No expiry</div>}
                  {cert.lastNotifiedAt && (
                    <div style={{ fontSize: 10.5, color: '#92400e' }}>
                      <Mail size={10} style={{ verticalAlign: -1, marginRight: 3 }} />
                      Last reminder: {fmt(cert.lastNotifiedAt)}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {cert.fileName && (
                    <button onClick={() => downloadFile(cert)} style={actBtn}>
                      <Download size={12} /> File
                    </button>
                  )}
                  {isAdmin && (
                    <>
                      <button onClick={() => { setEditing(cert); setShowForm(true); }} style={actBtn}>
                        <Edit3 size={12} /> Edit
                      </button>
                      <button onClick={() => setRenewing(cert)} style={{ ...actBtn, background: theme.success + '18', color: theme.success, borderColor: theme.success + '44' }}>
                        <RefreshCw size={12} /> Renew
                      </button>
                      <button onClick={() => handleDelete(cert)} style={{ ...actBtn, background: theme.danger + '15', color: theme.danger, borderColor: theme.danger + '44' }}>
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <CertFormModal
          API_BASE={API_BASE}
          cert={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); showMsg('success', 'Saved'); }}
        />
      )}

      {renewing && (
        <RenewModal
          API_BASE={API_BASE}
          cert={renewing}
          onClose={() => setRenewing(null)}
          onSaved={() => { setRenewing(null); load(); showMsg('success', 'Renewed — expiry reset, next reminder re-scheduled'); }}
        />
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

// ── Add/Edit Modal ──
const CertFormModal = ({ API_BASE, cert, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: cert?.name || '',
    category: cert?.category || 'license',
    issuer: cert?.issuer || '',
    certNumber: cert?.certNumber || '',
    issuedDate: cert?.issuedDate ? new Date(cert.issuedDate).toISOString().slice(0, 10) : '',
    expiryDate: cert?.expiryDate ? new Date(cert.expiryDate).toISOString().slice(0, 10) : '',
    neverExpires: cert?.neverExpires || false,
    notes: cert?.notes || '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (cert?._id) {
        await axios.put(`${API_BASE}/certificates/${cert._id}`, form);
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        if (file) fd.append('file', file);
        await axios.post(`${API_BASE}/certificates`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div style={modalBg} onClick={onClose}>
      <form onSubmit={submit} style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, color: theme.navy }}>{cert ? 'Edit Certificate' : 'Add Certificate'}</h3>
          <button type="button" onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <Field label="Name *" ><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} placeholder="e.g. BMW Certificate" /></Field>
          <Field label="Category">
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle}>
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Issuer"><input value={form.issuer} onChange={e => setForm({ ...form, issuer: e.target.value })} style={inputStyle} /></Field>
            <Field label="Cert Number"><input value={form.certNumber} onChange={e => setForm({ ...form, certNumber: e.target.value })} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Issued Date"><input type="date" value={form.issuedDate} onChange={e => setForm({ ...form, issuedDate: e.target.value })} style={inputStyle} /></Field>
            <Field label="Expiry Date"><input type="date" value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} disabled={form.neverExpires} style={{ ...inputStyle, opacity: form.neverExpires ? 0.5 : 1 }} /></Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={form.neverExpires} onChange={e => setForm({ ...form, neverExpires: e.target.checked })} />
            No expiry (perpetual certificate)
          </label>
          <Field label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, fontFamily: 'inherit' }} /></Field>
          {!cert && (
            <Field label="Upload File (PDF / image)">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} />
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>Max 25 MB</div>
            </Field>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button type="button" onClick={onClose} style={{ ...btnStyle, marginTop: 0, background: 'white', color: theme.navy, border: `1px solid ${theme.border}` }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...btnStyle, marginTop: 0 }}>
            {saving ? 'Saving…' : (cert ? 'Update' : 'Add Certificate')}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Renew Modal ──
const RenewModal = ({ API_BASE, cert, onClose, onSaved }) => {
  const [form, setForm] = useState({
    issuedDate: new Date().toISOString().slice(0, 10),
    expiryDate: '',
    certNumber: cert.certNumber || '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.expiryDate) return alert('New expiry date required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('file', file);
      await axios.post(`${API_BASE}/certificates/${cert._id}/renew`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved();
    } catch (e) { alert('Renew failed: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div style={modalBg} onClick={onClose}>
      <form onSubmit={submit} style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, color: theme.navy }}>Renew — {cert.name}</h3>
          <button type="button" onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10, fontSize: 12.5, color: '#92400e', marginBottom: 12 }}>
          Previous expiry: {fmt(cert.expiryDate)}. Renewal will reset notification flags — fresh reminders will trigger from 30 days before new expiry.
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="New Issued Date *"><input required type="date" value={form.issuedDate} onChange={e => setForm({ ...form, issuedDate: e.target.value })} style={inputStyle} /></Field>
            <Field label="New Expiry Date *"><input required type="date" value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} style={inputStyle} /></Field>
          </div>
          <Field label="New Cert Number"><input value={form.certNumber} onChange={e => setForm({ ...form, certNumber: e.target.value })} style={inputStyle} /></Field>
          <Field label="Upload Renewed File"><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} /></Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button type="button" onClick={onClose} style={{ ...btnStyle, marginTop: 0, background: 'white', color: theme.navy, border: `1px solid ${theme.border}` }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...btnStyle, marginTop: 0, background: theme.success }}>
            {saving ? 'Saving…' : 'Confirm Renewal'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: theme.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>
    {children}
  </div>
);

// ── styles ──
const headBtn = {
  ...btnStyle, marginTop: 0,
  background: 'rgba(255,255,255,0.18)',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.4)',
};
const pill = (active, color = theme.primary) => ({
  padding: '6px 12px',
  borderRadius: 999,
  border: `1px solid ${active ? color : theme.border}`,
  background: active ? color + '18' : 'white',
  color: active ? color : theme.textMuted,
  fontWeight: 600,
  fontSize: 12.5,
  cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center',
});
const pillCount = { marginLeft: 6, fontSize: 11, fontWeight: 700, background: 'rgba(0,0,0,0.08)', padding: '1px 6px', borderRadius: 999 };
const actBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '5px 10px', borderRadius: 6,
  border: `1px solid ${theme.primary}33`,
  background: theme.primary + '12',
  color: theme.primary,
  cursor: 'pointer',
  fontSize: 11.5, fontWeight: 600,
};
const iconBtn = { padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: theme.textMuted };
const modalBg = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalStyle = { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 520, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' };
const modalHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 };

export default CertificatesPage;
